import { Link } from "react-router-dom";

interface FooterProps {
  variant?: "default" | "compact";
}

const FOOTER_LINKS = [
  { to: "/about", label: "About" },
  { to: "/faq", label: "FAQ" },
  { to: "/terms", label: "Terms" },
  { to: "/privacy", label: "Privacy" },
] as const;

const CONTACT_EMAIL = "letters@eunice.world";

/**
 * Site footer with navigation links
 * - default: Full footer for homepage with separators
 * - compact: Minimal footer for content pages
 */
const Footer = ({ variant = "default" }: FooterProps) => {
  const isCompact = variant === "compact";

  return (
    <footer className={
      isCompact 
        ? "py-8 px-6 border-t border-border/30" 
        : "py-8 md:py-10 px-4 md:px-6 bg-cream-dark/50"
    }>
      <div className={`max-w-4xl mx-auto ${isCompact ? "flex flex-col md:flex-row items-center justify-between gap-4" : "text-center"}`}>
        {isCompact && (
          <Link to="/" className="font-serif text-lg text-foreground hover:text-primary transition-colors">
            Letters from Eunice
          </Link>
        )}
        
        <nav 
          className={`flex ${isCompact ? "items-center gap-6 text-sm text-muted-foreground" : "flex-wrap items-center justify-center gap-x-4 gap-y-2 md:gap-6 mb-4"}`}
          aria-label="Footer navigation"
        >
          {!isCompact && (
            <>
              <Link to="/about" className="text-sm text-charcoal-light hover:text-primary hover:underline transition-colors font-sans">
                About
              </Link>
              <Separator />
            </>
          )}
          
          {isCompact && (
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
          )}
          
          <Link 
            to="/faq" 
            className={isCompact ? "hover:text-foreground transition-colors" : "text-sm text-charcoal-light hover:text-primary hover:underline transition-colors font-sans"}
          >
            FAQ
          </Link>
          
          {!isCompact && <Separator />}
          
          <a 
            href={`mailto:${CONTACT_EMAIL}`} 
            className={isCompact ? "hover:text-foreground transition-colors" : "text-sm text-charcoal-light hover:text-primary hover:underline transition-colors font-sans"}
          >
            Contact
          </a>
          
          {!isCompact && <Separator />}
          
          <Link 
            to="/terms" 
            className={isCompact ? "hover:text-foreground transition-colors" : "text-sm text-charcoal-light hover:text-primary hover:underline transition-colors font-sans"}
          >
            Terms
          </Link>
          
          {!isCompact && <Separator />}
          
          <Link 
            to="/privacy" 
            className={isCompact ? "hover:text-foreground transition-colors" : "text-sm text-charcoal-light hover:text-primary hover:underline transition-colors font-sans"}
          >
            Privacy
          </Link>
        </nav>
        
        <p className={isCompact ? "text-sm text-muted-foreground" : "text-xs text-muted-foreground font-sans"}>
          © 2025{!isCompact && " Eunice"}
        </p>
      </div>
    </footer>
  );
};

/** Visual separator dot for default footer */
const Separator = () => (
  <span className="text-muted-foreground/50 hidden sm:inline" aria-hidden="true">•</span>
);

export default Footer;
